import { Children, createContext, isValidElement, useContext, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/** True inside a FilterBar: pickers render as compact filter triggers instead of full-width form fields. */
export const FilterBarContext = createContext(false);
export const useInFilterBar = () => useContext(FilterBarContext);

export const filterTriggerCls =
  'inline-flex h-9 w-full items-center gap-2 rounded-lg border border-border bg-surface px-3 text-left text-[13px] text-text transition-colors hover:border-subtle focus:outline-none focus-visible:border-muted disabled:opacity-60 sm:w-44';

export function useOutsideClose(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && close();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  return ref;
}

/** Compact trigger: icon + "By …" placeholder (muted) or the selected value, chevron or clear button. */
export function FilterTrigger({
  open,
  placeholder,
  valueLabel,
  icon,
  onClear,
  onClick,
  id,
  ariaLabel,
  disabled,
  trailingIcon,
}: {
  open: boolean;
  placeholder: string;
  valueLabel?: ReactNode;
  icon?: ReactNode;
  onClear?: () => void;
  onClick: () => void;
  id?: string;
  ariaLabel?: string;
  disabled?: boolean;
  trailingIcon?: ReactNode;
}) {
  const active = valueLabel !== undefined && valueLabel !== null && valueLabel !== '';
  return (
    <button
      id={id}
      type="button"
      disabled={disabled}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-label={ariaLabel}
      onClick={onClick}
      className={cn(filterTriggerCls, active && 'border-muted/60 bg-surface-2 font-medium', open && 'border-muted shadow-sm')}
    >
      {icon && <span className="shrink-0 text-subtle">{icon}</span>}
      <span className={cn('min-w-0 flex-1 truncate', !active && 'text-subtle')}>{active ? valueLabel : placeholder}</span>
      {active && onClear ? (
        <span
          role="button"
          tabIndex={0}
          aria-label="Clear filter"
          className="shrink-0 rounded p-0.5 text-subtle hover:bg-surface-3 hover:text-text"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onClear();
            }
          }}
        >
          <X className="h-3.5 w-3.5" />
        </span>
      ) : (
        (trailingIcon ?? <ChevronDown className={cn('h-4 w-4 shrink-0 text-subtle transition-transform duration-150', open && 'rotate-180')} aria-hidden />)
      )}
    </button>
  );
}

export function FilterPopover({ children, className, align = 'left' }: { children: ReactNode; className?: string; align?: 'left' | 'right' }) {
  return (
    <div className={cn('absolute z-40 mt-1.5 w-max min-w-full max-w-72 rounded-xl border border-border bg-surface p-1 shadow-lg animate-scale-in', align === 'right' ? 'right-0' : 'left-0', className)}>
      {children}
    </div>
  );
}

export function FilterOption({ selected, onClick, children, multi }: { selected: boolean; onClick: () => void; children: ReactNode; multi?: boolean }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onClick}
      className={cn('flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-[13px] transition-colors hover:bg-surface-2', selected && 'bg-surface-2 font-medium')}
    >
      {multi && (
        <span className={cn('flex h-4 w-4 shrink-0 items-center justify-center rounded border', selected ? 'border-primary bg-primary text-primary-fg' : 'border-border')}>
          {selected && <Check className="h-3 w-3" />}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {!multi && selected && <Check className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />}
    </button>
  );
}

export function PopoverSearch({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative mb-1">
      <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-subtle" aria-hidden />
      <input
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search…"
        aria-label="Search options"
        className="h-8 w-full rounded-lg border border-border bg-surface-2 pr-2 pl-8 text-[13px] focus:border-muted focus:bg-surface focus:outline-none"
      />
    </div>
  );
}

function byLabel(label: string) {
  return /^by\s/i.test(label) ? label : `By ${label}`;
}

interface Opt {
  value: string;
  label: string;
}

function optionsFrom(children: ReactNode): Opt[] {
  const out: Opt[] = [];
  const walk = (nodes: ReactNode) =>
    Children.forEach(nodes, (c) => {
      if (!isValidElement(c)) return;
      const el = c as ReactElement<{ value?: string | number; children?: ReactNode }>;
      if (el.type === 'option') out.push({ value: String(el.props.value ?? ''), label: textOf(el.props.children) });
      else if (el.props.children) walk(el.props.children);
    });
  walk(children);
  return out.filter((o) => o.value !== '');
}

function textOf(n: ReactNode): string {
  if (n === null || n === undefined || typeof n === 'boolean') return '';
  if (typeof n === 'string' || typeof n === 'number') return String(n);
  if (Array.isArray(n)) return n.map(textOf).join('');
  if (isValidElement(n)) return textOf((n.props as { children?: ReactNode }).children);
  return '';
}

/**
 * Compact single-select filter. Accepts <option> children (so existing usages keep working)
 * and renders "By <label>" with a small popover list; a search box appears for long lists.
 */
export function FilterSelect({ value, onChange, label, children, className, icon }: { value?: string; onChange: (v: string) => void; label: string; children: ReactNode; className?: string; icon?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useOutsideClose(open, () => setOpen(false));
  const options = useMemo(() => optionsFrom(children), [children]);
  const selected = options.find((o) => o.value === (value ?? ''));
  const visible = q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options;
  useEffect(() => {
    if (!open) setQ('');
  }, [open]);
  return (
    <div ref={ref} className={cn('relative w-full sm:w-auto', className)}>
      <FilterTrigger
        open={open}
        placeholder={byLabel(label)}
        valueLabel={selected?.label ?? (value ? value : undefined)}
        icon={icon}
        ariaLabel={`${label} filter`}
        onClick={() => setOpen((o) => !o)}
        onClear={() => onChange('')}
        trailingIcon={open && options.length > 6 ? <Search className="h-3.5 w-3.5 shrink-0 text-subtle" aria-hidden /> : undefined}
      />
      {open && (
        <FilterPopover>
          {options.length > 6 && <PopoverSearch value={q} onChange={setQ} />}
          <div role="listbox" aria-label={label} className="scrollbar-thin max-h-64 overflow-y-auto">
            {visible.length === 0 && <p className="px-3 py-2 text-xs text-muted">No matches</p>}
            {visible.map((o) => (
              <FilterOption
                key={o.value}
                selected={o.value === value}
                onClick={() => {
                  onChange(o.value === value ? '' : o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </FilterOption>
            ))}
          </div>
        </FilterPopover>
      )}
    </div>
  );
}

/** Compact multi-select filter (values stored as a comma list by callers). */
export function FilterMultiSelect({ label, options, value, onChange, icon }: { label: string; options: { id: string; label: string }[]; value: string[]; onChange: (v: string[]) => void; icon?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useOutsideClose(open, () => setOpen(false));
  const summary = value.length === 0 ? undefined : value.length === 1 ? (options.find((o) => o.id === value[0])?.label ?? value[0]) : `${byLabel(label).slice(3)} · ${value.length}`;
  return (
    <div ref={ref} className="relative w-full sm:w-auto">
      <FilterTrigger open={open} placeholder={byLabel(label)} valueLabel={summary} icon={icon} ariaLabel={`${label} filter`} onClick={() => setOpen((o) => !o)} onClear={() => onChange([])} />
      {open && (
        <FilterPopover>
          <div role="listbox" aria-multiselectable aria-label={label} className="scrollbar-thin max-h-64 overflow-y-auto">
            {options.map((o) => {
              const on = value.includes(o.id);
              return (
                <FilterOption key={o.id} multi selected={on} onClick={() => onChange(on ? value.filter((v) => v !== o.id) : [...value, o.id])}>
                  {o.label}
                </FilterOption>
              );
            })}
          </div>
        </FilterPopover>
      )}
    </div>
  );
}

/** Small pill toggle group, e.g. Day / Week / Month. */
export function SegmentedControl<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: { id: T; label: string }[]; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex h-9 items-center rounded-lg border border-border bg-surface-2 p-0.5">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          onClick={() => onChange(o.id)}
          className={cn(
            'h-full rounded-md px-3 text-xs font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            value === o.id ? 'bg-surface text-text shadow-sm' : 'text-muted hover:text-text',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
